import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import deletemodelpopup from "../../assets/task/deletemodelpopup.svg";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import clockOuticon from "../../assets/task/login_svgrepo-com.svg";
import {
    ApolloError,
    useMutation,
} from "@apollo/client";
import { useAuth } from "../../context/AuthContext";
import { ADMIN_CREATE_CLOCK_IN } from "../../api/mutation";
import { showError, showSuccess } from "../common/ToastNotification";
import InputsNew from "../common/InputsNew";
import Loader from "../common/Loader";

interface MarkPresentPopupProps {
    isOpen: boolean;
    onClose: () => void;
    selectedDate: string;
    employeeId: string;
    refetch?: () => void;
}

const markPresentSchema = yup.object().shape({
    startTime: yup.string().required("Start time is required"),
    endTime: yup.string().required("End time is required"),
});

const MarkPresentPopup: React.FC<MarkPresentPopupProps> = ({
    isOpen,
    onClose,
    selectedDate,
    employeeId,
    refetch
}) => {
    const { userInfo } = useAuth();
    const agencyId = userInfo?.agencies?.id || '';
    const appUserID = userInfo?.app_user_id || "";
    const currentEmployeeID = userInfo?.employee_id || '';

    // API
    const [createClockIn, { loading: createClockInLoading }] = useMutation(ADMIN_CREATE_CLOCK_IN, {
        fetchPolicy: 'no-cache',
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<any>({
        resolver: yupResolver(markPresentSchema),
    });

    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("coverflow_hidden_scoll_bdy");
            return () => {
                document.body.classList.remove("coverflow_hidden_scoll_bdy");
            };
        }
    }, [isOpen]);

    const handleCancel = () => {
        reset();
        onClose();
    };

    const formatDateTime = (date: string, time: string) => {
        return `${date}T${time}:00.000Z`;
    };

    async function onSubmit(data: any) {
        const checkInTime = formatDateTime(selectedDate, data.startTime);
        const checkOutTime = formatDateTime(selectedDate, data.endTime);

        // Calculate duration
        const startDate = new Date(checkInTime);
        const endDate = new Date(checkOutTime);
        const diffMinutes = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60));
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        const clockInInput = {
            employee_id: parseInt(employeeId),
            check_in_time: checkInTime,
            check_out_time: checkOutTime,
            agency_id: parseInt(agencyId),
            checked_by: currentEmployeeID,
            minutes: minutes,
            hours: hours,
            created_by: currentEmployeeID,
            app_user_id: parseInt(appUserID),
        };

        try {
            const response = await createClockIn({ variables: { input: clockInInput } });

            if (response.data.admin_create_clock_in?.status_code == 201) {
                handleCancel();
                showSuccess(response.data.admin_create_clock_in.message)
                if (refetch) refetch();
            }

            if (response.data.admin_create_clock_in.errors?.length > 0) {
                showError(response.data.admin_create_clock_in.errors[0].messages[0]);
            }

            if (response.data.admin_create_clock_in?.status_code == 500) {
                showError(response.data.admin_create_clock_in.message);
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message);
            }
        }
    }

    if (!isOpen) return null;

    return (
        <>
            <div id="model-main-blg" onClick={(e) => e.stopPropagation()}>
                <div id="inner_loader_dv">
                    <div className="clockout_time_main w-[480px] flex flex-row items-start justify-start max-w-[622px] z-[1] mq825:max-w-full m-4 schedularaddtask_main">
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="m-0 flex-1 rounded-radius-radius-lg bg-white border-[1px] border-solid border-surface-border flex flex-col items-center justify-start p-8 mq575:p-3 box-border gap-[23.8px] max-w-full mq825:pt-[21px] mq825:pb-[21px] mq825:box-border"
                        >
                            <div className="relative w-full" onClick={handleCancel}>
                                <img
                                    src={deletemodelpopup}
                                    alt=""
                                    className="cursor-pointer pr-0 mq575:pr-0 mq575:pt-0 w-5 h-5 absolute right-0 top-[-5px] mq575:top-0 mq575:w-3 mq575:h-3 dark-white-img"
                                    onClick={handleCancel}
                                />
                            </div>

                            <div className="clockout_time_blg w-full flex justify-center items-center">
                                <div className="grid gap-[8px] p-[10px] text-center">
                                    <h3 className="my-0 dark:text-white">Mark as Present</h3>
                                    <h5 className="my-0 dark:text-white">{selectedDate}</h5>
                                </div>
                            </div>

                            <div className="flex flex-col gap-[16px] w-full">
                                <InputsNew
                                    label="Start Time*"
                                    type="time"
                                    contentPlaceholder="Select start time"
                                    register={{ ...register("startTime") }}
                                    errorMessage={errors.startTime}
                                    autoComplete="off"
                                />

                                <InputsNew
                                    label="End Time*"
                                    type="time"
                                    contentPlaceholder="Select end time"
                                    register={{ ...register("endTime") }}
                                    errorMessage={errors.endTime}
                                    autoComplete="off"
                                />
                            </div>

                            <div className="self-stretch flex flex-row items-center justify-center py-0 pr-0 gap-[8px] mq450:box-border mq950:box-border mq1425:flex-wrap mq1425:box-border">
                                <button
                                    className="w-full cursor-pointer [border:none] pxz-spacing-lg bg-surface-primary-surface py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button flex flex-row items-center justify-center box-border gap-[12px] hover:bg-dodgerblue"
                                    type="submit"
                                >
                                    <img src={clockOuticon} alt="" />
                                    <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block min-w-[51px]">
                                        MARK PRESENT
                                    </div>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            {createClockInLoading && <Loader />}
        </>
    );
};

export default MarkPresentPopup;